
import React, { useState, useContext, Fragment, useEffect } from "react";
import { useAuth } from "../hooks/AuthProvider";
import { useNavigate, useSearchParams } from "react-router-dom";
import '../Pages/Signup.css'; 
import Logo from "../assets/image/logoo.png"; 
import Alert from '../Components/Alert';
import { LanguageContext } from '../context/LanguageContext';
import authTranslations from '../languages/auth.json';
import signupImg from "../assets/image/65214547de2a3ff968379537926d89705ec4f0a1.png"

const EmailIcon = ({ className }) => (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
);
const KeyIcon = ({ className }) => (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="15" r="4"></circle><path d="M14.5 9c-1.76-1.57-4.24-1.57-6 0"></path></svg>
);
const LockIcon = ({ className }) => (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
);
const ChevronLeft = ({ isRtl }) => (
    <svg style={{ transform: isRtl ? 'rotate(180deg)' : 'none' }} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
);

export const VerifyCode = () => {
    const { lang } = useContext(LanguageContext);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const auth = useAuth();
    
    const currentLangData = authTranslations[lang] || authTranslations['en'];
    const t = currentLangData.verifyEmail || currentLangData.signup || {}; 
    const isRtl = ['ar', 'ur'].includes(lang);

    const initialEmail = searchParams.get('email') || '';
    const [email] = useState(initialEmail);
    const [code, setCode] = useState("");
    const [password, setPassword] = useState("");
    const [application, setApplication] = useState("");
    const [submitting, setSubmitting] = useState(false);
    
    const [alertState, setAlertState] = useState({
        open: false,
        message: '',
        type: 'info',
        position: isRtl ? 'left' : 'right',
    });

    useEffect(() => {
        if (!initialEmail) {
            navigate('/signup');
        }
    }, [initialEmail, navigate]);

    const showAlert = (type, message) => {
        setAlertState({ open: true, type, message, position: isRtl ? 'left' : 'right' });
    };

    const onFormSubmit = async (e) => {
        e.preventDefault();
        
        if (!code.trim()) {
            showAlert('error', t.errorRequiredCode || (lang === 'ar' ? "الرمز مطلوب" : "Code is required"));
            return;
        }
        if (password.length < 8) { 
            showAlert('error', t.errorPasswordLength || (lang === 'ar' ? "يجب أن تكون كلمة المرور 8 أحرف" : "Password must be at least 8 characters"));
            return;
        }

        setSubmitting(true);
        try {
            const resp = await auth.verifyAction(email, code, password, application);
            if(resp.success) {
                showAlert('success', lang === 'ar' ? 'تم تفعيل الحساب بنجاح!' : 'Account verified successfully!');
                setTimeout(() => navigate('/login'), 2000);
            } else {
                showAlert('error', resp.message || (lang === 'ar' ? 'فشل التحقق' : 'Verification failed.'));
            }
        } catch (error) {
            showAlert('error', error.message || 'Error occurred');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Fragment>
            <Alert
                open={alertState.open}
                close={() => setAlertState({ ...alertState, open: false })}
                message={alertState.message}
                type={alertState.type}
                position={alertState.position}
            />
            
            <div className="auth-page-wrapper" dir={isRtl ? "rtl" : "ltr"}>
                <div className="auth-brand-header">
                    <img src={Logo} alt="Sama-IOT Logo" className="auth-brand-logo" />
                    <span className="auth-brand-name">Sama-IOT</span>
                </div>

                <div className="signup-card-center">
                    <div className="signup-main-card">
                        <div className="signup-form-area">
                            
                            <button className="signup-back-button" onClick={() => navigate(-1)}>
                                <ChevronLeft isRtl={isRtl} />
                                {currentLangData.signup?.back || (lang === 'ar' ? 'رجوع' : 'Back')}
                            </button>

                            <div className="signup-header-text">
                                <h2>{t.title || (lang === 'ar' ? 'تأكيد الحساب' : 'Verify Account')}</h2>
                                <p className="auth-subtitle">
                                    {t.instructionText || (lang === 'ar' ? 'أدخل الرمز المرسل إلى' : 'Enter the code sent to')} <strong>{email}</strong>
                                </p>
                            </div>
                            
                            <form onSubmit={onFormSubmit}>
                                
                                <div className="auth-form-group">
                                    <label htmlFor="auth-email">
                                        <EmailIcon className="auth-label-icon" />
                                        {t.labelEmail || (lang === 'ar' ? "البريد الإلكتروني" : "Email")}
                                    </label>
                                    <div className="auth-input-wrapper">
                                        <input
                                            id="auth-email"
                                            value={email}
                                            type="email"
                                            readOnly
                                            className="auth-form-input disabled-input"
                                            style={{ backgroundColor: '#f9fafb', cursor: 'not-allowed' }}
                                        />
                                    </div>
                                </div>
                                
                                <div className="auth-form-group">
                                    <label htmlFor="auth-code">
                                        <KeyIcon className="auth-label-icon" />
                                        {t.labelCode || (lang === 'ar' ? "رمز التحقق" : "Verification Code")}
                                    </label>
                                    <div className="auth-input-wrapper">
                                        <input
                                            id="auth-code"
                                            value={code}
                                            type="text"
                                            placeholder={t.placeholderCode || (lang === 'ar' ? "أدخل الرمز هنا" : "Enter code")}
                                            onChange={ev => setCode(ev.target.value)}
                                            required
                                            className="auth-form-input"
                                            disabled={submitting}
                                        />
                                    </div>
                                </div>
                                
                                <div className="auth-form-group">
                                    <label htmlFor="auth-password">
                                        <LockIcon className="auth-label-icon" />
                                        {t.labelPassword || (lang === 'ar' ? "كلمة المرور الجديدة" : "New Password")}
                                    </label>
                                    <div className="auth-input-wrapper">
                                        <input
                                            id="auth-password"
                                            type="password"
                                            value={password}
                                            placeholder={t.placeholderPassword || (lang === 'ar' ? "اختر كلمة مرور" : "Password")}
                                            onChange={ev => setPassword(ev.target.value)}
                                            required
                                            className="auth-form-input"
                                            disabled={submitting}
                                        />
                                    </div>
                                </div>

                                <div className="auth-form-group">
                                    <label htmlFor="verify-optional-text">
                                        {t.labelApplication || (lang === 'ar' ? "Ø§Ù„ØªØ·Ø¨ÙŠÙ‚" : "Application")}
                                    </label>
                                    <div className="auth-input-wrapper">
                                        <textarea
                                            id="verify-optional-text"
                                            value={application}
                                            placeholder={t.applicationPlaceholder || (lang === 'ar' ? "Ø§Ù„ØªØ·Ø¨ÙŠÙ‚ (Ø§Ø®ØªÙŠØ§Ø±ÙŠ)" : "Application (optional)")}
                                            onChange={ev => setApplication(ev.target.value)}
                                            className="auth-form-input"
                                            rows={3}
                                            maxLength={4000}
                                            disabled={submitting}
                                            style={{ resize: 'vertical' }}
                                        />
                                    </div>
                                </div>

                                <button
                                    className="auth-submit-btn"
                                    type="submit"
                                    disabled={submitting}
                                >
                                    {submitting 
                                        ? (t.buttonVerifying || (lang === 'ar' ? 'جاري التحقق...' : 'Verifying...')) 
                                        : (t.buttonVerify || (lang === 'ar' ? 'تأكيد الحساب' : 'Verify Account'))
                                    }
                                </button>

                            </form>
                        </div>
                        
                        <div className="signup-image-area">
                            <img 
                                src={signupImg} 
                                alt="Verify Illustration" 
                                className="signup-illustration"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};
